/** Tick renderer of the category axis: wrapped lines or a 40° turn. */
import React from 'react';
import { CHART_AXIS_FONT_SIZE } from './FmChartChrome.constants';
import type { FmCategoryAxisLayout } from './categoryAxis.constants';
import { CATEGORY_LINE_H, CATEGORY_ROTATE_DEG, CATEGORY_TICK_GAP } from './categoryAxis.constants';

export interface TickProps {
  x?: number | string;
  y?: number | string;
  payload?: { value?: unknown };
  layout: FmCategoryAxisLayout;
  format?: (value: string) => string;
}

/** The Recharts tick renderer for `categoryAxisLayout`. */
export const FmCategoryTick: React.FC<TickProps> = ({ x, y, payload, layout, format }) => {
  const raw = String(payload?.value ?? '');
  const label = format ? format(raw) : raw;
  const px = Number(x ?? 0);
  const py = Number(y ?? 0);
  if (layout.mode === 'rotate') {
    const keep = layout.maxRotatedChars ?? label.length;
    const shown = label.length > keep ? `${label.slice(0, Math.max(1, keep - 1))}…` : label;
    return (
      <text
        x={px}
        y={py + CATEGORY_TICK_GAP}
        textAnchor="end"
        transform={`rotate(-${CATEGORY_ROTATE_DEG} ${px} ${py + CATEGORY_TICK_GAP})`}
        fill="var(--fm-chart-label)"
        fontSize={CHART_AXIS_FONT_SIZE}
      >
        {shown === label ? null : <title>{label}</title>}
        {shown}
      </text>
    );
  }
  const lines = layout.lines.get(label) ?? [label];
  return (
    <text
      x={px}
      y={py + CATEGORY_TICK_GAP}
      textAnchor="middle"
      fill="var(--fm-chart-label)"
      fontSize={CHART_AXIS_FONT_SIZE}
    >
      {lines.map((line, index) => (
        <tspan key={line} x={px} dy={index === 0 ? 0 : CATEGORY_LINE_H}>
          {line}
        </tspan>
      ))}
    </text>
  );
};
